import React from "react";
import "./style.scss";
import GoBack from "../../../assets/GoBackApp.svg";
import { useNavigate } from "react-router-dom";
import { PATH_LIST } from "shared/lib/react-router";
import { useStore } from "zustand";
import { useTranslation } from "react-i18next";
import { UserStatus } from "../components/account-status";
import { sessionStore } from "entities/session/model/sessionModel";
import { marketStore } from "entities/market";

type TariffV2Row = {
  id?: number | string;
  market_group?: string;
  instrument_key?: string;
  instrument_name?: string;
  min_commission?: number | string | null;
  min_commission_currency?: string | null;
  min_or_equivalent?: boolean;
  standard_commission?: number | string | null;
  standard_custody?: number | string | null;
  prime_commission?: number | string | null;
  prime_custody?: number | string | null;
  prime_plus_commission?: number | string | null;
  prime_plus_custody?: number | string | null;
  commission_type?: string;
  plus_bank_fees?: boolean;
  commission_max?: number | null;
  sort_order?: number;
};

type TariffTableRow = {
  key: string | number;
  title: string;
  minCommission: string;
  commission: string;
  custody: string;
};

type TariffTranslate = (key: string, options?: any) => unknown;

const toText = (value: unknown) =>
  typeof value === "string" ? value : String(value ?? "");

const GROUP_LABELS: Record<string, string> = {
  AMX_CDA:
    "Armenian Stock Exchange (AMX) / Central Depository of Armenia (CDA)",
  GLOBAL_FI: "Global Markets — Fixed Income",
  EQUITIES_NA: "Global Markets — Equities: North America",
  EQUITIES_EU: "Global Markets — Equities: Europe",
  EQUITIES_ASIA_ME: "Global Markets — Equities: Asia & Middle East",
  DERIVATIVES: "Derivatives",
  FX: "FX Operations",
  FOP: "Securities Transfers (Free-of-Payment)",
  CASH: "Cash Deposit / Withdrawal Operations",
  OTC: "OTC",
  GLOBAL_EXCHANGES: "Global Markets — Fixed Income",
};

const COMMISSION_TYPE_LABELS: Record<string, string> = {
  free: "tariffsCommissionTypeFree",
  embedded: "tariffsCommissionTypeEmbedded",
  // on_request: "tariffsCommissionTypeOnRequest",
  on_request: "—",
  bank_fees: "tariffsCommissionTypeBankFeesOnly",
  range: "tariffsCommissionTypeRange",
};

const formatPercent = (value?: number | string | null) => {
  if (value === null || value === undefined || value === "") return "";
  const numberValue = Number(value);
  if (Number.isNaN(numberValue)) return String(value);
  return `${(numberValue * 100).toFixed(3).replace(/\.?0+$/, "")}%`;
};

const MIN_COMMISSION_CURRENCY_LABELS_AM: Record<string, string> = {
  AMD: "ՀՀԴ",
  USD: "ԱՄՆ դոլար",
  CAD: "Կանադական դոլար",
  EUR: "եվրո",
  HKD: "Հոնկոնգի դոլար (HKD)",
  JPY: "ճապոնական իեն (JPY)",
  SGD: "սինգապուրյան դոլար (SGD)",
  AED: "ԱՄԷ դիրհամ (AED)",
  QAR: "քաթարյան ռիալ (QAR)",
  SAR: "սաուդյան ռիալ (SAR)",
  OMR: "օմանյան ռիալ (OMR)",
};

const getLocalizedMinCurrency = (
  currency?: string | null,
  language?: string,
) => {
  if (!currency) return "";
  const code = (language || "en").toLowerCase().split("-")[0];
  if (code !== "am" && code !== "hy") return currency;
  return MIN_COMMISSION_CURRENCY_LABELS_AM[currency] || currency;
};

const formatMinCommission = (
  row: TariffV2Row,
  t: TariffTranslate,
  language?: string,
) => {
  if (row.min_commission === null || row.min_commission === undefined)
    return "";
  const currencyLabel = getLocalizedMinCurrency(
    row.min_commission_currency,
    language,
  );
  const base = `${row.min_commission}${
    currencyLabel ? ` ${currencyLabel}` : ""
  }`;
  return row.min_or_equivalent
    ? `${base} ${toText(t("tariffsOrEquivalent"))}`
    : base;
};

const getV2Sections = (
  rows: TariffV2Row[],
  tariffID: number,
  t: TariffTranslate,
  language?: string,
) => {
  const sorted = [...rows].sort(
    (a, b) => (a.sort_order || 0) - (b.sort_order || 0),
  );
  const grouped = sorted.reduce<Record<string, TariffV2Row[]>>((acc, row) => {
    const key = row.market_group || "OTHER";
    if (!acc[key]) acc[key] = [];
    acc[key].push(row);
    return acc;
  }, {});

  return Object.entries(grouped).map(([group, groupRows]) => {
    const rowsByGroup: TariffTableRow[] = groupRows.map((row) => {
      const commissionType = row.commission_type || "";
      const isPercent = commissionType === "percent";
      const isRange = commissionType === "range";
      const typeLabel = toText(
        t(COMMISSION_TYPE_LABELS[commissionType] || commissionType || ""),
      );

      const formatCommission = (commission?: number | string | null) => {
        if (isPercent) {
          const base = formatPercent(commission);
          return row.plus_bank_fees
            ? `${base} ${toText(t("tariffsPlusBankFees"))}`
            : base;
        }
        if (isRange && commission != null) {
          const minPct = formatPercent(commission);
          const maxPct =
            row.commission_max != null ? formatPercent(row.commission_max) : "";
          const langCode = (language || "en").toLowerCase().split("-")[0];
          const isArmenian = langCode === "am" || langCode === "hy";
          const rangeStr = maxPct
            ? isArmenian
              ? `${minPct} ${toText(t("tariffsRangeFrom"))} ${toText(
                  t("tariffsRangeTo"),
                )} ${maxPct}`
              : `${toText(t("tariffsRangeFrom"))} ${minPct} ${toText(
                  t("tariffsRangeTo"),
                )} ${maxPct}`
            : minPct;
          return row.plus_bank_fees
            ? `${rangeStr} ${toText(t("tariffsPlusBankFees"))}`
            : rangeStr;
        }
        return typeLabel;
      };

      return {
        key: row.instrument_key || row.id || Math.random(),
        title: toText(
          t(row.instrument_key || "", {
            defaultValue: row.instrument_name || "",
          }),
        ),
        minCommission:
          isPercent || isRange ? formatMinCommission(row, t, language) : "",
        commission:
          tariffID === 0
            ? formatCommission(row.standard_commission)
            : tariffID === 1
            ? formatCommission(row.prime_commission)
            : formatCommission(row.prime_plus_commission),
        custody:
          tariffID === 0
            ? formatPercent(row.standard_custody)
            : tariffID === 1
            ? formatPercent(row.prime_custody)
            : formatPercent(row.prime_plus_custody),
      };
    });

    return {
      key: `group-${group}`,
      title: GROUP_LABELS[group] || group,
      rows: rowsByGroup,
    };
  });
};

export const TariffsPage: React.FC = () => {
  const { t, i18n } = useTranslation("app");
  const { user } = useStore(sessionStore);
  const navigate = useNavigate();
  const handleNavigate = () => {
    navigate(PATH_LIST.account);
  };

  const { tariffs } = useStore(marketStore);
  const tariffID = user!.tariff - 1;

  if (!tariffs || !tariffs.length || !user || !user?.tariff || tariffID < 0) {
    return <></>;
  }

  const tariffSections = getV2Sections(
    tariffs as TariffV2Row[],
    tariffID,
    t,
    i18n.language,
  );
  const activeTariffLabel =
    tariffID === 0
      ? toText(t("standard", { defaultValue: "ՍՏԱՆԴԱՐՏ" }))
      : tariffID === 1
      ? toText(t("prime", { defaultValue: "ՓՐԱՅՄ" }))
      : toText(t("primePlus", { defaultValue: "ՓՐԱՅՄ+" }));

  return (
    <div className="acc-tariffs">
      <div className="acc-tariffs-header">
        <img
          className="go-back-tariffs"
          onClick={handleNavigate}
          src={GoBack}
          alt="Go Back"
        />
        <p>{t("myTariffs")}</p>
        <UserStatus status={user?.tariff} />
      </div>
      <div className="acc-tariffs-main">
        <div className="tariffs-mobile-table">
          <div className="tariffs-mobile-table__head">
            <div className="tariffs-mobile-table__head-left">
              <div className="title-cell">
                {toText(
                  t("tariffsTableInstrumentMarket", {
                    defaultValue: "Գործիք / Շուկա",
                  }),
                )}
              </div>
              <div>
                {toText(
                  t("tariffsTableMinCommission", {
                    defaultValue: "Նվազագույն միջնորդավճար մեկ գործարքի համար",
                  }),
                )}
              </div>
            </div>
            <div className="tariffs-mobile-table__head-right">
              <div>
                {toText(
                  t("tariffsTableCommission", {
                    defaultValue: "Միջնորդավճար",
                  }),
                )}
              </div>
              <div>
                {toText(
                  t("tariffsTableCustodyPerYear", {
                    defaultValue: "Պահառության վճար (տարեկան)",
                  }),
                )}
              </div>
            </div>
          </div>
          <div className="tariffs-mobile-table__body">
            {tariffSections.map((section) => (
              <div key={section.key} className="tariff-section">
                <div className="group-title">{toText(t(section.title))}</div>
                {section.rows.map((row) => (
                  <div key={row.key} className="tariff-row">
                    <div className="tariff-row__left">
                      <div>{row.title}</div>
                      <div>{row.minCommission || "—"}</div>
                    </div>
                    <div className="tariff-row__right">
                      <div>{row.commission || "—"}</div>
                      <div>{row.custody || "—"}</div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
